/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: L'Édition — Living Index Controller
   Project: La Troupe
   Version: 1.3
   Status: LLCL-DRAFT

   RESPONSIBILITIES
   ----------------------------------------------------------
   DESKTOP
   - Preserve Living Index behavior
   - Activate collection from vertical scroll
   - Hover / focus preview
   - Smooth navigation between chapters

   MOBILE
   - 100svh sticky campaign stage
   - Vertical scroll drives editorial chapters
   - Image crossfade
   - Copy enters from right
   - Previous copy exits left
   - Counter synchronization
   - Progress synchronization
   - No scroll hijacking
   - No carousel
   - No external dependencies

   SHOPIFY
   - Multiple section instances safe
   - Theme Editor block selection
   - Section load / unload support
   - Breakpoint changes
   - Reduced motion support
   ========================================================== */

(() => {
  'use strict';


  /* ========================================================
     00. PREVENT DUPLICATE GLOBAL INITIALIZATION
     ======================================================== */

  const GLOBAL_KEY = 'LLEditionSystem';


  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll();
    return;
  }



  /* ========================================================
     01. CONSTANTS
     ======================================================== */

  const SELECTORS = {
    root: '[data-ll-edition]',

    story:
      '[data-ll-edition-story]',

    stage:
      '[data-ll-edition-stage]',

    step:
      '[data-ll-edition-step]',

    select:
      '[data-ll-edition-select]',

    media:
      '[data-ll-edition-media]',

    mobileCopy:
      '[data-ll-edition-mobile-copy]',

    progress:
      '[data-ll-edition-progress]',

    counter:
      '[data-ll-edition-counter]'
  };


  const BREAKPOINTS = {
    mobile:
      '(max-width: 699px)',

    desktop:
      '(min-width: 700px)',

    reducedMotion:
      '(prefers-reduced-motion: reduce)'
  };


  const FOCAL_POINTS = {
    /*
      Desktop:
      slightly above center because image occupies
      the right column while text lives left.
    */
    desktop:
      0.44,

    /*
      Mobile:
      deliberately high.

      This means the current visual remains visible
      for most of its scroll chapter before the next
      scene replaces it.
    */
    mobile:
      0.34
  };



  /* ========================================================
     02. UTILITIES
     ======================================================== */

  const clamp = (
    value,
    min,
    max
  ) => {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  };


  const pad = value => {
    return String(value).padStart(
      2,
      '0'
    );
  };


  const prefersReducedMotion = () => {
    return window
      .matchMedia(
        BREAKPOINTS.reducedMotion
      )
      .matches;
  };


  const getViewportHeight = () => {
    return (
      window.innerHeight
      ||
      document.documentElement.clientHeight
      ||
      800
    );
  };



  /* ========================================================
     03. CONTROLLER
     ======================================================== */

  class LLEditionController {


    constructor(root) {

      this.root =
        root;


      this.sectionId =
        root.dataset.sectionId
        ||
        root.id
        ||
        'll-edition';


      this.story =
        root.querySelector(
          SELECTORS.story
        );


      this.stage =
        root.querySelector(
          SELECTORS.stage
        );


      this.steps =
        Array.from(
          root.querySelectorAll(
            SELECTORS.step
          )
        );


      this.medias =
        Array.from(
          root.querySelectorAll(
            SELECTORS.media
          )
        );


      this.mobileCopies =
        Array.from(
          root.querySelectorAll(
            SELECTORS.mobileCopy
          )
        );


      this.progress =
        root.querySelector(
          SELECTORS.progress
        );


      this.counter =
        root.querySelector(
          SELECTORS.counter
        );


      this.mobileQuery =
        window.matchMedia(
          BREAKPOINTS.mobile
        );


      this.desktopQuery =
        window.matchMedia(
          BREAKPOINTS.desktop
        );


      this.motionQuery =
        window.matchMedia(
          BREAKPOINTS.reducedMotion
        );


      this.activeIndex =
        -1;


      this.previousIndex =
        -1;


      this.rafId =
        null;


      this.resizeTimer =
        null;


      this.destroyed =
        false;


      this.cleanupCallbacks =
        [];


      this.handleScroll =
        this.handleScroll.bind(this);


      this.handleResize =
        this.handleResize.bind(this);


      this.handleBreakpointChange =
        this.handleBreakpointChange.bind(this);


      this.handleBlockSelect =
        this.handleBlockSelect.bind(this);


      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);

    }



    /* ======================================================
       04. INITIALIZE
       ====================================================== */

    init() {

      if (
        this.steps.length === 0
        ||
        this.medias.length === 0
      ) {

        return;
      }


      /*
        Prevent accidental re-initialization
        of the same section instance.
      */

      if (
        this.root.dataset.llEditionInitialized
        ===
        'true'
      ) {

        return;
      }


      this.root.dataset.llEditionInitialized =
        'true';


      this.root.classList.add(
        'is-js-ready'
      );


      this.syncThemeMetrics();

      this.bindGlobalEvents();

      this.bindStepInteractions();

      this.bindBreakpointEvents();


      /*
        The Liquid already renders the first frame
        active. JS now synchronizes every state.
      */

      this.setActive(
        0,
        {
          force: true,
          initial: true
        }
      );


      /*
        Important for:
        - browser scroll restoration
        - Shopify editor refresh
        - anchor navigation
      */

      requestAnimationFrame(
        () => {
          this.updateFromScroll();
        }
      );

    }



    /* ======================================================
       05. GENERIC EVENT REGISTRATION
       ====================================================== */

    on(
      target,
      type,
      handler,
      options
    ) {

      if (!target) {
        return;
      }


      target.addEventListener(
        type,
        handler,
        options
      );


      this.cleanupCallbacks.push(
        () => {

          target.removeEventListener(
            type,
            handler,
            options
          );

        }
      );

    }



    /* ======================================================
       06. GLOBAL EVENTS
       ====================================================== */

    bindGlobalEvents() {

      this.on(
        window,
        'scroll',
        this.handleScroll,
        {
          passive: true
        }
      );


      this.on(
        window,
        'resize',
        this.handleResize,
        {
          passive: true
        }
      );


      this.on(
        document,
        'shopify:block:select',
        this.handleBlockSelect
      );


      this.on(
        document,
        'visibilitychange',
        this.handleVisibilityChange
      );


      /*
        bfcache / Safari restoration.
      */

      this.on(
        window,
        'pageshow',
        () => {
          this.requestUpdate();
        },
        {
          passive: true
        }
      );

    }



    /* ======================================================
       07. BREAKPOINT EVENTS
       ====================================================== */

    bindBreakpointEvents() {

      const queries = [
        this.mobileQuery,
        this.desktopQuery,
        this.motionQuery
      ];


      queries.forEach(query => {

        if (
          typeof query.addEventListener
          ===
          'function'
        ) {

          query.addEventListener(
            'change',
            this.handleBreakpointChange
          );


          this.cleanupCallbacks.push(
            () => {

              query.removeEventListener(
                'change',
                this.handleBreakpointChange
              );

            }
          );

        } else if (
          typeof query.addListener
          ===
          'function'
        ) {

          query.addListener(
            this.handleBreakpointChange
          );


          this.cleanupCallbacks.push(
            () => {

              query.removeListener(
                this.handleBreakpointChange
              );

            }
          );

        }

      });

    }



    /* ======================================================
       08. STEP INTERACTIONS
       DESKTOP ONLY
       ====================================================== */

    bindStepInteractions() {

      this.steps.forEach(
        (
          step,
          index
        ) => {


          const selector =
            step.querySelector(
              SELECTORS.select
            );


          if (!selector) {
            return;
          }



          /* -----------------------------------------------
             POINTER PREVIEW
             ----------------------------------------------- */

          this.on(
            selector,
            'pointerenter',
            () => {

              if (
                !this.desktopQuery.matches
              ) {

                return;
              }


              this.setActive(
                index
              );

            }
          );



          /* -----------------------------------------------
             KEYBOARD
             ----------------------------------------------- */

          this.on(
            selector,
            'focus',
            () => {

              if (
                !this.desktopQuery.matches
              ) {

                return;
              }


              this.setActive(
                index
              );

            }
          );



          /* -----------------------------------------------
             CLICK → ALIGN CHAPTER
             ----------------------------------------------- */

          this.on(
            selector,
            'click',
            event => {

              /*
                Button exists as the editorial selector,
                not as the collection destination.
              */

              event.preventDefault();


              if (
                !this.desktopQuery.matches
              ) {

                return;
              }


              this.setActive(
                index
              );


              this.scrollStepIntoReadingPosition(
                step
              );

            }
          );


        }
      );

    }



    /* ======================================================
       09. SET ACTIVE EDITION
       ====================================================== */

    setActive(
      index,
      options = {}
    ) {

      if (
        this.destroyed
        ||
        this.steps.length === 0
      ) {

        return;
      }


      const safeIndex =
        clamp(
          index,
          0,
          this.steps.length - 1
        );


      if (
        safeIndex === this.activeIndex
        &&
        !options.force
      ) {

        return;
      }


      const oldIndex =
        this.activeIndex;


      this.previousIndex =
        oldIndex;


      this.activeIndex =
        safeIndex;



      /* ----------------------------------------------------
         DIRECTION
         ---------------------------------------------------- */

      let direction =
        'initial';


      if (
        oldIndex >= 0
        &&
        safeIndex > oldIndex
      ) {

        direction =
          'forward';

      }


      if (
        oldIndex >= 0
        &&
        safeIndex < oldIndex
      ) {

        direction =
          'backward';

      }


      this.root.dataset.editionDirection =
        direction;


      this.root.dataset.activeEdition =
        String(
          safeIndex
        );



      /* ----------------------------------------------------
         STEPS
         ---------------------------------------------------- */

      this.steps.forEach(
        (
          step,
          stepIndex
        ) => {


          const active =
            stepIndex === safeIndex;


          step.classList.toggle(
            'is-active',
            active
          );


          step.classList.toggle(
            'is-before',
            stepIndex < safeIndex
          );


          step.classList.toggle(
            'is-after',
            stepIndex > safeIndex
          );


          const selector =
            step.querySelector(
              SELECTORS.select
            );


          if (selector) {

            selector.setAttribute(
              'aria-pressed',
              active
                ?
                'true'
                :
                'false'
            );

          }

        }
      );



      /* ----------------------------------------------------
         CAMPAIGN MEDIA
         ---------------------------------------------------- */

      this.medias.forEach(
        (
          media,
          mediaIndex
        ) => {


          const active =
            mediaIndex === safeIndex;


          media.classList.toggle(
            'is-active',
            active
          );


          media.classList.toggle(
            'is-before',
            mediaIndex < safeIndex
          );


          media.classList.toggle(
            'is-after',
            mediaIndex > safeIndex
          );


          media.setAttribute(
            'aria-hidden',
            active
              ?
              'false'
              :
              'true'
          );

        }
      );



      /* ----------------------------------------------------
         MOBILE EDITORIAL COPY

         Past chapters stay conceptually left.
         Future chapters stay conceptually right.

         This is what creates:
           old copy ←
           new copy →
      ---------------------------------------------------- */

      this.mobileCopies.forEach(
        (
          copy,
          copyIndex
        ) => {


          const active =
            copyIndex === safeIndex;


          copy.classList.toggle(
            'is-active',
            active
          );


          copy.classList.toggle(
            'is-before',
            copyIndex < safeIndex
          );


          copy.classList.toggle(
            'is-after',
            copyIndex > safeIndex
          );


          copy.setAttribute(
            'aria-hidden',
            active
              ?
              'false'
              :
              'true'
          );

        }
      );



      /* ----------------------------------------------------
         COUNTER
         ---------------------------------------------------- */

      this.updateCounter(
        safeIndex
      );



      /* ----------------------------------------------------
         PROGRESS
         ---------------------------------------------------- */

      this.updateProgress(
        safeIndex
      );



      /* ----------------------------------------------------
         ACCESSIBILITY ANNOUNCEMENT STATE
         ---------------------------------------------------- */

      this.root.setAttribute(
        'data-current-edition',
        pad(
          safeIndex + 1
        )
      );

    }



    /* ======================================================
       10. COUNTER
       ====================================================== */

    updateCounter(index) {

      if (!this.counter) {
        return;
      }


      const current =
        pad(
          index + 1
        );


      const total =
        pad(
          this.steps.length
        );


      this.counter.textContent =
        `${current} / ${total}`;

    }



    /* ======================================================
       11. PROGRESS
       ====================================================== */

    updateProgress(index) {

      if (!this.progress) {
        return;
      }


      /*
        First chapter should already have visible
        progress.

        3 chapters:
        01 = 33.33%
        02 = 66.66%
        03 = 100%
      */

      const percentage =
        (
          (
            index + 1
          )
          /
          this.steps.length
        )
        *
        100;


      this.progress.style.width =
        `${percentage}%`;

    }



    /* ======================================================
       12. SCROLL UPDATE REQUEST
       ====================================================== */

    handleScroll() {
      this.requestUpdate();
    }


    requestUpdate() {

      if (
        this.destroyed
        ||
        this.rafId !== null
      ) {

        return;
      }


      this.rafId =
        requestAnimationFrame(
          () => {

            this.rafId =
              null;


            this.updateFromScroll();

          }
        );

    }



    /* ======================================================
       13. SCROLL → ACTIVE CHAPTER
       ====================================================== */

    updateFromScroll() {

      if (
        this.destroyed
        ||
        this.steps.length === 0
      ) {

        return;
      }


      const viewportHeight =
        getViewportHeight();


      /*
        Ignore expensive geometry calculations while
        the section is very far from the viewport.
      */

      const rootRect =
        this.root.getBoundingClientRect();


      const safetyDistance =
        viewportHeight
        *
        1.5;


      if (
        rootRect.bottom
        <
        -safetyDistance
        ||
        rootRect.top
        >
        viewportHeight
        +
        safetyDistance
      ) {

        return;
      }



      const focalRatio =
        this.mobileQuery.matches
          ?
          FOCAL_POINTS.mobile
          :
          FOCAL_POINTS.desktop;


      const focalPoint =
        viewportHeight
        *
        focalRatio;



      /*
        Primary strategy:
        find which scroll chapter currently contains
        the editorial focal point.
      */

      let containedIndex =
        -1;


      for (
        let index = 0;
        index < this.steps.length;
        index += 1
      ) {


        const rect =
          this.steps[
            index
          ].getBoundingClientRect();


        if (
          rect.top
          <=
          focalPoint
          &&
          rect.bottom
          >
          focalPoint
        ) {

          containedIndex =
            index;

          break;

        }

      }



      if (
        containedIndex >= 0
      ) {

        this.setActive(
          containedIndex
        );

        return;

      }



      /*
        Fallback:
        determine closest chapter.

        Needed during:
        - section entrance
        - section exit
        - resize
        - very fast scrolling
      */

      let closestIndex =
        0;


      let closestDistance =
        Infinity;


      this.steps.forEach(
        (
          step,
          index
        ) => {


          const rect =
            step.getBoundingClientRect();


          const center =
            rect.top
            +
            (
              rect.height
              *
              0.5
            );


          const distance =
            Math.abs(
              center
              -
              focalPoint
            );


          if (
            distance
            <
            closestDistance
          ) {

            closestDistance =
              distance;


            closestIndex =
              index;

          }

        }
      );


      this.setActive(
        closestIndex
      );

    }



    /* ======================================================
       14. DESKTOP CLICK NAVIGATION
       ====================================================== */

    scrollStepIntoReadingPosition(step) {

      if (!step) {
        return;
      }


      const rect =
        step.getBoundingClientRect();


      const viewportHeight =
        getViewportHeight();


      const focalPoint =
        viewportHeight
        *
        FOCAL_POINTS.desktop;


      const stepCenter =
        rect.top
        +
        (
          rect.height
          *
          0.5
        );


      const movement =
        stepCenter
        -
        focalPoint;


      const destination =
        window.scrollY
        +
        movement;


      window.scrollTo({

        top:
          Math.max(
            0,
            destination
          ),


        behavior:
          prefersReducedMotion()
            ?
            'auto'
            :
            'smooth'

      });

    }



    /* ======================================================
       15. THEME METRICS
       ====================================================== */

    syncThemeMetrics() {

      /*
        Kept available as a system variable even though
        the visual stage intentionally sticks at top: 0.

        Useful for:
        - editor navigation
        - future header-aware variants
        - debugging
      */

      const htmlStyles =
        getComputedStyle(
          document.documentElement
        );


      const headerHeight =
        parseFloat(
          htmlStyles.getPropertyValue(
            '--header-height'
          )
        )
        ||
        0;


      const headerSticky =
        parseFloat(
          htmlStyles.getPropertyValue(
            '--header-is-sticky'
          )
        )
        ||
        0;


      const announcementHeight =
        parseFloat(
          htmlStyles.getPropertyValue(
            '--announcement-bar-height'
          )
        )
        ||
        0;


      const announcementSticky =
        parseFloat(
          htmlStyles.getPropertyValue(
            '--announcement-bar-is-sticky'
          )
        )
        ||
        0;


      const themeHeaderOffset =
        (
          headerHeight
          *
          headerSticky
        )
        +
        (
          announcementHeight
          *
          announcementSticky
        );


      this.root.style.setProperty(
        '--ll-edition-theme-header',
        `${themeHeaderOffset}px`
      );

    }



    /* ======================================================
       16. RESIZE
       ====================================================== */

    handleResize() {

      clearTimeout(
        this.resizeTimer
      );


      this.resizeTimer =
        setTimeout(
          () => {

            if (
              this.destroyed
            ) {

              return;
            }


            this.syncThemeMetrics();

            this.requestUpdate();

          },
          100
        );

    }



    /* ======================================================
       17. BREAKPOINT CHANGE
       ====================================================== */

    handleBreakpointChange() {

      if (
        this.destroyed
      ) {

        return;
      }


      this.syncThemeMetrics();


      /*
        Reset directional state before re-evaluating
        the current chapter.
      */

      this.root.dataset.editionDirection =
        'initial';


      this.requestUpdate();

    }



    /* ======================================================
       18. DOCUMENT VISIBILITY
       ====================================================== */

    handleVisibilityChange() {

      if (
        document.visibilityState
        ===
        'visible'
      ) {

        this.requestUpdate();

      }

    }



    /* ======================================================
       19. SHOPIFY BLOCK SELECTION
       ====================================================== */

    handleBlockSelect(event) {

      if (
        this.destroyed
        ||
        !event.detail
      ) {

        return;
      }


      const eventSectionId =
        String(
          event.detail.sectionId
          ||
          ''
        );


      const ownSectionId =
        String(
          this.sectionId
          ||
          ''
        );


      if (
        eventSectionId
        !==
        ownSectionId
      ) {

        return;
      }


      const blockId =
        String(
          event.detail.blockId
          ||
          ''
        );


      const selectedIndex =
        this.steps.findIndex(
          step => {

            return (
              String(
                step.dataset.blockId
                ||
                ''
              )
              ===
              blockId
            );

          }
        );


      if (
        selectedIndex < 0
      ) {

        return;
      }


      this.setActive(
        selectedIndex,
        {
          force: true
        }
      );


      const selectedStep =
        this.steps[
          selectedIndex
        ];


      /*
        Theme Editor:

        Center the invisible mobile trigger or
        visible desktop chapter.

        The sticky stage will update automatically.
      */

      if (selectedStep) {

        selectedStep.scrollIntoView({

          behavior:
            prefersReducedMotion()
              ?
              'auto'
              :
              'smooth',

          block:
            'center'

        });

      }

    }



    /* ======================================================
       20. DESTROY
       ====================================================== */

    destroy() {

      if (
        this.destroyed
      ) {

        return;
      }


      this.destroyed =
        true;


      if (
        this.rafId !== null
      ) {

        cancelAnimationFrame(
          this.rafId
        );


        this.rafId =
          null;

      }


      clearTimeout(
        this.resizeTimer
      );


      this.cleanupCallbacks.forEach(
        cleanup => {

          try {

            cleanup();

          } catch (error) {

            /*
              Never allow Shopify editor cleanup
              to break the storefront.
            */

          }

        }
      );


      this.cleanupCallbacks =
        [];


      delete this.root.dataset.llEditionInitialized;


      this.root.classList.remove(
        'is-js-ready'
      );


      if (
        this.root._llEditionController
        ===
        this
      ) {

        delete this.root._llEditionController;

      }

    }


  }



  /* ========================================================
     21. SYSTEM MANAGER
     ======================================================== */

  const system = {


    controllers:
      new Set(),



    initRoot(root) {

      if (
        !root
        ||
        root._llEditionController
      ) {

        return;
      }


      const controller =
        new LLEditionController(
          root
        );


      root._llEditionController =
        controller;


      controller.init();


      this.controllers.add(
        controller
      );

    },



    initAll(scope = document) {

      /*
        Scope itself may be the section root.
      */

      if (
        scope.matches
        &&
        scope.matches(
          SELECTORS.root
        )
      ) {

        this.initRoot(
          scope
        );

      }


      /*
        Or it may contain one or several editions.
      */

      if (
        typeof scope.querySelectorAll
        ===
        'function'
      ) {

        scope
          .querySelectorAll(
            SELECTORS.root
          )
          .forEach(
            root => {

              this.initRoot(
                root
              );

            }
          );

      }

    },



    destroySection(sectionId) {

      this.controllers.forEach(
        controller => {


          if (
            String(
              controller.sectionId
            )
            !==
            String(
              sectionId
            )
          ) {

            return;

          }


          controller.destroy();


          this.controllers.delete(
            controller
          );

        }
      );

    }


  };



  /* ========================================================
     22. EXPOSE SYSTEM
     ======================================================== */

  window[
    GLOBAL_KEY
  ] =
    system;



  /* ========================================================
     23. INITIAL PAGE LOAD
     ======================================================== */

  const boot = () => {

    system.initAll(
      document
    );

  };


  if (
    document.readyState
    ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true
      }
    );

  } else {

    boot();

  }



  /* ========================================================
     24. SHOPIFY SECTION LOAD
     ======================================================== */

  document.addEventListener(
    'shopify:section:load',
    event => {

      if (!event.target) {
        return;
      }


      system.initAll(
        event.target
      );

    }
  );



  /* ========================================================
     25. SHOPIFY SECTION UNLOAD
     ======================================================== */

  document.addEventListener(
    'shopify:section:unload',
    event => {


      if (
        !event.detail
        ||
        !event.detail.sectionId
      ) {

        return;

      }


      system.destroySection(
        event.detail.sectionId
      );

    }
  );


})();